/**
 * The staff live map's own reads (docs/specs/cloud/live-tracking.md §2, §7): the project's hazard pins and landholder
 * records (their staff policies, 20261027005000 and 20261027006000) and its hazard pack (`hazard_pack` for the
 * version, then `staff_hazard_pack` for the KML when the version changed; 20261027190000) and its shape file layers
 * (`shape_layer`, 20261102180000; R152, decision 921). Everything else on the map
 * is read by the Cloud's other maps' own reads (`loadProjects`, `loadProjectPlan`, `useProjectAircraft`,
 * `useCloudFires`).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  parcelsOfRecords,
  portalHazards,
  shapeLayersOfRecords,
  type ShapeLayerRecord,
  type HazardMarkView,
  type HazardPinRecord,
  type LandParcel,
  type LandParcelRecord,
  type PortalHazardLayer,
} from '@teslyn/domain';
import { all, plainPortalError, type Res } from '../portal/api.ts';

/** The project's live hazard pins, as Teslyn Field's Map reads them. */
export async function loadHazardPins(sb: SupabaseClient, projectId: string): Promise<HazardPinRecord[]> {
  return all<HazardPinRecord>(
    (from, to) =>
      sb
        .from('hazard_pin')
        .select('*')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('name')
        .range(from, to) as unknown as PromiseLike<Res<HazardPinRecord[]>>,
  );
}

/**
 * The project's hazard marks (R243), read by the staff policy of `hazard_mark` (20261105140000); none when Teslyn Cloud
 * does not have the table yet.
 */
export async function loadHazardMarks(sb: SupabaseClient, projectId: string): Promise<HazardMarkView[]> {
  try {
    return await all<HazardMarkView>(
      (from, to) =>
        sb
          .from('hazard_mark')
          .select('hazard_key, state, marked_at, marked_by_name, deleted_at')
          .eq('project_id', projectId)
          .order('hazard_key')
          .range(from, to) as unknown as PromiseLike<Res<HazardMarkView[]>>,
    );
  } catch (e) {
    if (/PGRST205|42P01|could not find the table|does not exist/i.test(String((e as Error)?.message ?? e)))
      return [];
    throw e;
  }
}

/** The project's landholder properties (every one shown: hiding one is a laptop's own choice). */
export async function loadLandParcels(sb: SupabaseClient, projectId: string): Promise<LandParcel[]> {
  const rows = await all<LandParcelRecord>(
    (from, to) =>
      sb
        .from('land_parcel')
        .select('*')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('name')
        .range(from, to) as unknown as PromiseLike<Res<LandParcelRecord[]>>,
  );
  return parcelsOfRecords(rows, new Set());
}

/**
 * The project's shape file layers (hazards and other layers), as Teslyn Field's Map reads them; none when Teslyn Cloud
 * does not have the table yet (the migration not applied: PostgREST's PGRST205, Postgres 42P01).
 */
export async function loadShapeLayers(sb: SupabaseClient, projectId: string): Promise<ShapeLayerRecord[]> {
  try {
    const rows = await all<Record<string, unknown>>(
      (from, to) =>
        sb
          .from('shape_layer')
          .select('id, project_id, name, kind, source, crs, features, created_at, updated_at, deleted_at')
          .eq('project_id', projectId)
          .is('deleted_at', null)
          .order('name')
          .range(from, to) as unknown as PromiseLike<Res<Record<string, unknown>[]>>,
    );
    return shapeLayersOfRecords(rows);
  } catch (e) {
    if (/PGRST205|42P01|could not find the table|does not exist/i.test(String((e as Error)?.message ?? e)))
      return [];
    throw e;
  }
}

/** The project's current hazard pack as the staff map draws it. */
export interface StaffPack {
  readonly name: string;
  readonly version: number;
  readonly sha256: string;
  readonly publishedAt: string;
  /** The pack's hazards (`portalHazards` without the landholder properties: the records are drawn instead). */
  readonly layers: readonly PortalHazardLayer[];
}

/** A function Teslyn Cloud does not have yet (the migration not applied): PostgREST's PGRST202, Postgres 42883. */
const notThereYet = (e: { code?: string; message?: string } | null): boolean =>
  !!e &&
  (e.code === 'PGRST202' || e.code === '42883' || /could not find the function/i.test(e.message ?? ''));

/**
 * The project's current hazard pack, read again only when its version changed since `known` (a pack is up to 10 MB).
 * Null: no pack, or Teslyn Cloud does not have `staff_hazard_pack` yet (nothing is said).
 */
export async function loadStaffPack(
  sb: SupabaseClient,
  projectId: string,
  known: StaffPack | null,
): Promise<StaffPack | null> {
  const head = (await sb
    .from('hazard_pack')
    .select('version, sha256')
    .eq('project_id', projectId)) as unknown as Res<{ version: number; sha256: string }[]>;
  if (head.error) throw new Error(plainPortalError(head.error));
  const row = head.data?.[0];
  if (!row) return null;
  if (known && known.version === row.version && known.sha256 === row.sha256) return known;
  const r = (await sb.rpc('staff_hazard_pack', { p_project: projectId })) as {
    data: unknown;
    error: { code?: string; message: string } | null;
  };
  if (notThereYet(r.error)) return null;
  if (r.error) throw new Error(plainPortalError(r.error));
  const d = r.data as {
    name?: unknown;
    version?: unknown;
    sha256?: unknown;
    published_at?: unknown;
    kml?: unknown;
  } | null;
  if (!d || typeof d.kml !== 'string') return null;
  const read = portalHazards(d.kml, { landholders: false });
  return {
    name: typeof d.name === 'string' ? d.name : '',
    version: typeof d.version === 'number' ? d.version : row.version,
    sha256: typeof d.sha256 === 'string' ? d.sha256 : row.sha256,
    publishedAt: typeof d.published_at === 'string' ? d.published_at : '',
    layers: read?.layers ?? [],
  };
}
